import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  Box
} from '@mui/material';
import { X as CloseIcon } from 'lucide-react';
import languageLearningApi from '../../middleware/languageLearningApi';

const SectionForm = ({ courseId, section, onClose, defaultOrder, existingSections, onCascadeUpdate }) => {
  const [title, setTitle] = useState(section ? section.title : '');
  const [description, setDescription] = useState(section ? section.description : '');
  const [order, setOrder] = useState(section ? section.order : defaultOrder || 1);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    // In creation mode, ensure the order is unique.
    if (!section && existingSections && existingSections.some(sec => sec.order === order)) {
      setError('A section with this order already exists.');
      return;
    }
    // Enforce maximum allowed order: max existing order + 1.
    if (existingSections && order > Math.max(...existingSections.map(sec => sec.order)) + 1) {
      setError(`Order cannot be higher than ${Math.max(...existingSections.map(sec => sec.order)) + 1}.`);
      return;
    }
    // In edit mode, if order is changed, cascade update affected sections.
    if (section && section.order !== order && onCascadeUpdate) {
      await onCascadeUpdate(order, section._id);
    }

    const sectionData = { title, description, order };
    try {
      let response;
      if (section && section._id) {
        response = await languageLearningApi.put(`/sections/${section._id}`, sectionData);
      } else {
        response = await languageLearningApi.post(`/courses/${courseId}/sections`, sectionData);
      }
      const data = response.data;
      if (data.success) {
        onClose();
      }
    } catch (err) {
      console.error("Error saving section:", err);
    }
  };

  const handleOrderChange = (e) => {
    const newOrder = parseInt(e.target.value) || 1;
    setOrder(newOrder);
    setError('');
    if (!section && existingSections) {
      if (existingSections.some(sec => sec.order === newOrder)) {
        setError('A section with this order already exists.');
      }
      const maxAllowed = Math.max(...existingSections.map(sec => sec.order)) + 1;
      if (newOrder > maxAllowed) {
        setError(`Order cannot be higher than ${maxAllowed}.`);
      }
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, position: 'relative', maxWidth: '100%' }} elevation={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {section ? 'Edit Section' : 'Add Section'}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon size={18} />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 3 }} />
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Title"
            variant="outlined"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Enter section title"
          />
          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            size="small"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter section description"
          />
          <TextField
            fullWidth
            label="Order"
            variant="outlined"
            type="number"
            size="small"
            value={order}
            onChange={handleOrderChange}
            required
            inputProps={{ min: 1 }}
            error={Boolean(error)}
            helperText={error}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Submit
            </Button>
          </Stack>
        </Stack>
      </form>
    </Paper>
  );
};

export default SectionForm;
